import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Table,
  Form,
  Row,
  Col,
  Button,
  Badge,
  Modal,
  InputGroup,
  FormControl,
} from "react-bootstrap";
import "../styles/AuditLog.css";

const ACTION_COLORS = {
  CREATE_VENDOR: "success",
  DELETE_VENDOR: "danger",
  UPDATE_STATUS: "primary",
  UPDATE_COM_MODE: "info",
  ADD_ACTION: "secondary",
  UPDATE_ACTION: "warning",
  DELETE_ACTION: "danger",
  LOGIN: "dark",
  ADD_CATEGORY: "success",
  ADD_SUBCATEGORY: "success",
  ADD_SOURCE: "success",
  ADD_REQUIREMENT: "success",
  REGISTER_USER: "primary",
  EXPORT_EXCEL: "secondary",
  EXPORT_HISTORY_EXCEL: "secondary",
};

const ENTITY_TYPES = [
  "",
  "VendorForm",
  "VendorActions",
  "Category",
  "SubCategory",
  "Source",
  "Requirement",
  "Users",
];
const ACTIONS = [
  "",
  "CREATE_VENDOR",
  "DELETE_VENDOR",
  "UPDATE_STATUS",
  "UPDATE_COM_MODE",
  "ADD_ACTION",
  "UPDATE_ACTION",
  "DELETE_ACTION",
  "LOGIN",
  "REGISTER_USER",
  "ADD_CATEGORY",
  "ADD_SUBCATEGORY",
  "ADD_SOURCE",
  "ADD_REQUIREMENT",
  "EXPORT_EXCEL",
  "EXPORT_HISTORY_EXCEL",
];

const AuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [username, setUsername] = useState("");
  const [action, setAction] = useState("");
  const [entityType, setEntityType] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [limit, setLimit] = useState(200);

  // Detail modal
  const [showDetails, setShowDetails] = useState(false);
  const [detailContent, setDetailContent] = useState(null);

  const api_url = process.env.REACT_APP_API_URL;

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("token");
      const params = {};
      if (username) params.username = username;
      if (action) params.action = action;
      if (entityType) params.entityType = entityType;
      if (fromDate) params.from = fromDate;
      if (toDate) params.to = toDate;
      if (limit) params.limit = limit;

      const { data } = await axios.get(`${api_url}/api/audit`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setLogs(data);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch audit logs.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReset = () => {
    setUsername("");
    setAction("");
    setEntityType("");
    setFromDate("");
    setToDate("");
    setLimit(200);
  };

  const openDetails = (details) => {
    try {
      setDetailContent(JSON.parse(details));
    } catch {
      setDetailContent(details);
    }
    setShowDetails(true);
  };

  const formatTimestamp = (ts) => {
    if (!ts) return "-";
    // DB stores Sri Lanka time as a naive datetime string e.g. "2026-03-04T18:21:51.000"
    // We parse the parts directly to avoid any browser timezone interference.
    const str = typeof ts === "string" ? ts : new Date(ts).toISOString();
    const [datePart, timePart = ""] = str.split("T");
    const [year, month, day] = datePart.split("-");
    const time = timePart.split(".")[0]; // strip milliseconds
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${day} ${months[parseInt(month, 10) - 1]} ${year}, ${time}`;
  };

  return (
    <div className="container-fluid mt-4">
      <h2 className="mb-4">Audit Log</h2>

      {/* Filters */}
      <div className="card shadow-sm mb-4 audit-filter-card">
        <div className="card-body">
          <Row className="g-2">
            <Col xs={6} md={2}>
              <Form.Label className="fw-semibold">Username</Form.Label>
              <InputGroup>
                <FormControl
                  placeholder="Search username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="fw-semibold">Action</Form.Label>
              <Form.Select
                value={action}
                onChange={(e) => setAction(e.target.value)}
              >
                {ACTIONS.map((a) => (
                  <option key={a} value={a}>
                    {a || "All Actions"}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="fw-semibold">Entity Type</Form.Label>
              <Form.Select
                value={entityType}
                onChange={(e) => setEntityType(e.target.value)}
              >
                {ENTITY_TYPES.map((e) => (
                  <option key={e} value={e}>
                    {e || "All Entities"}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="fw-semibold">From</Form.Label>
              <Form.Control
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label className="fw-semibold">To</Form.Label>
              <Form.Control
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </Col>
            <Col xs={6} md={1}>
              <Form.Label className="fw-semibold">Limit</Form.Label>
              <Form.Control
                type="number"
                min={10}
                max={1000}
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
              />
            </Col>
            <Col
              xs={12}
              md={1}
              className="d-flex align-items-end gap-2 audit-filter-actions"
            >
              <Button
                variant="primary"
                onClick={fetchLogs}
                style={{ whiteSpace: "nowrap" }}
              >
                Apply
              </Button>
              <Button variant="outline-secondary" onClick={handleReset}>
                Reset
              </Button>
            </Col>
          </Row>
        </div>
      </div>

      {/* Stats bar */}
      <div className="mb-3 text-muted small">
        {loading
          ? "Loading..."
          : `Showing ${logs.length} record${logs.length !== 1 ? "s" : ""}`}
        {error && <span className="text-danger ms-3">{error}</span>}
      </div>

      {/* Table */}
      <div className="audit-table-wrapper" style={{ overflowX: "auto" }}>
        <Table bordered hover responsive size="sm" className="shadow-sm">
          <thead className="table-dark">
            <tr>
              <th>#</th>
              <th>Timestamp</th>
              <th>Username</th>
              <th>Scope</th>
              <th>Action</th>
              <th>Entity Type</th>
              <th>Entity ID</th>
              <th>IP Address</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {!loading && logs.length === 0 && (
              <tr>
                <td colSpan={9} className="text-center text-muted py-4">
                  No audit log entries found.
                </td>
              </tr>
            )}
            {logs.map((log, idx) => (
              <tr key={log.Id}>
                <td>{idx + 1}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  {formatTimestamp(log.Timestamp)}
                </td>
                <td>
                  <strong>{log.Username}</strong>
                </td>
                <td>
                  <Badge
                    bg={log.UserScope === "Local" ? "warning" : "info"}
                    text="dark"
                  >
                    {log.UserScope || "—"}
                  </Badge>
                </td>
                <td>
                  <Badge bg={ACTION_COLORS[log.Action] || "secondary"}>
                    {log.Action}
                  </Badge>
                </td>
                <td>{log.EntityType || "—"}</td>
                <td>{log.EntityId ?? "—"}</td>
                <td>{log.IPAddress || "—"}</td>
                <td>
                  {log.Details ? (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => openDetails(log.Details)}
                    >
                      View
                    </Button>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {/* Details Modal */}
      <Modal
        show={showDetails}
        onHide={() => setShowDetails(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Audit Entry Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <pre
            className="audit-detail-pre"
            style={{
              backgroundColor: "#f8f9fa",
              padding: "1rem",
              borderRadius: "6px",
              maxHeight: "400px",
              overflowY: "auto",
              fontSize: "0.85rem",
            }}
          >
            {JSON.stringify(detailContent, null, 2)}
          </pre>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetails(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AuditLog;
